import { Model } from 'dva'
import { message } from 'antd'
import { OrganisationsService } from '../../Service'
import { FailureHandler } from 'Utils/ApiHelper'

const Prompts: Model = {
  namespace: 'prompts',
  state: {
    undoStack: [],
    redoStack: [],
    promptsList: [],
    // For applications custom prompts
    overridePrompts: new Map<string, any[]>(),
    resetPromptsList: [],
    loading: true,
  },
  effects: {
    *fetchPrompts({ payload, onSuccess }, { call, put, select }): any {
      yield put({ type: 'toggleLoading', payload: true })
      const organisationUuid = yield select(
        (state: any) => state.flowLayout.flow?.organisation?.uuid
      )
      const flowUuid = yield select((state: any) => state.flowLayout.flow?.uuid)
      const version = yield select((state: any) => state.flowLayout.version?.version)
      const response = yield call(OrganisationsService.organisationsFlowsVersionsPromptsList, {
        organisationUuid,
        flowUuid,
        flowConfigVersion: version,
        ordering: '-id',
        ...payload,
      })
      onSuccess(response)
      yield put({
        type: 'savePromptsList',
        payload: response,
      })
      yield put({ type: 'toggleLoading', payload: false })
    },
    *retrievePrompt({ payload, onSuccess }, { call, put, select }): any {
      yield put({ type: 'toggleLoading', payload: true })
      const organisationUuid = yield select(
        (state: any) => state.flowLayout.flow?.organisation?.uuid
      )
      const flowUuid = yield select((state: any) => state.flowLayout.flow?.uuid)
      const version = yield select((state: any) => state.flowLayout.version?.version)
      try {
        const response = yield call(
          OrganisationsService.organisationsFlowsVersionsPromptsRetrieve,
          {
            organisationUuid,
            flowUuid,
            flowConfigVersion: version,
            ...payload,
          }
        )
        yield put({
          type: 'editPromptDetails',
          payload: response,
        })
      } catch (err) {
        console.error('Failed to retrieve prompt', err)
      }
      yield put({ type: 'toggleLoading', payload: false })
    },
    *createPrompt({ payload }, { call, put, select }): any {
      yield put({ type: 'toggleLoading', payload: true })
      const organisationUuid = yield select(
        (state: any) => state.flowLayout.flow?.organisation?.uuid
      )
      const flowUuid = yield select((state: any) => state.flowLayout.flow?.uuid)
      const flowConfigVersion = yield select((state: any) => state.flowLayout.version?.version)
      const response = yield call(OrganisationsService.organisationsFlowsVersionsPromptsCreate, {
        organisationUuid,
        flowUuid,
        flowConfigVersion,
        requestBody: payload,
      })

      if (response.status === 201) {
        message.success(`Added prompt`)
      } else {
        message.error(`Could not add prompt`)
      }
      yield put({
        type: 'addPromptContentToPromptsList',
        payload: response.data,
      })
      yield put({ type: 'toggleLoading', payload: false })
    },
    *createPromptContent(
      { payload, promptId, setLoading = () => {}, onSuccess = () => {}, onFailure = () => {} },
      { call, put, select }
    ): any {
      setLoading(true)
      const organisationUuid = yield select(
        (state: any) => state.flowLayout.flow?.organisation?.uuid
      )
      const flowUuid = yield select((state: any) => state.flowLayout.flow?.uuid)
      const flowConfigVersion = yield select((state: any) => state.flowLayout.version?.version)
      try {
        const response = yield call(
          OrganisationsService.organisationsFlowsVersionsPromptsPromptContentsCreate,
          {
            organisationUuid,
            flowUuid,
            flowConfigVersion,
            requestBody: payload,
            promptPk: promptId,
          }
        )
        yield put({
          type: 'addPromptContentToPromptsList',
          payload: { response, promptId },
        })
        onSuccess(response)
        return response
      } catch (err) {
        FailureHandler('Could not add prompt', err, onFailure)
      }
      setLoading(false)
    },
    *editPrompt({ payload, promptId }, { call, put, select }): any {
      yield put({ type: 'toggleLoading', payload: true })
      const organisationUuid = yield select(
        (state: any) => state.flowLayout.flow?.organisation?.uuid
      )
      const flowUuid = yield select((state: any) => state.flowLayout.flow?.uuid)
      const flowConfigVersion = yield select((state: any) => state.flowLayout.version?.version)
      try {
        const response = yield call(
          OrganisationsService.organisationsFlowsVersionsPromptsPartialUpdate,
          {
            organisationUuid,
            flowUuid,
            flowConfigVersion,
            requestBody: payload,
            id: promptId,
          }
        )
        if (response.status === 201 || 200) {
          message.success(`Edited prompt`)
        } else {
          message.error(`Could not edit prompt`)
        }
        yield put({ type: 'editPromptDetails', payload: response })
      } catch (e) {
        FailureHandler('Failed To Update Prompt', e)
      }
      yield put({ type: 'toggleLoading', payload: false })
    },
    *editPromptContent(
      { payload, id, promptId, setLoading = () => {}, onSuccess = () => {}, onFailure = () => {} },
      { call, put, select }
    ): any {
      setLoading(true)
      const organisationUuid = yield select(
        (state: any) => state.flowLayout.flow?.organisation?.uuid
      )
      const flowUuid = yield select((state: any) => state.flowLayout.flow?.uuid)
      const flowConfigVersion = yield select((state: any) => state.flowLayout.version?.version)
      try {
        const response = yield call(
          OrganisationsService.organisationsFlowsVersionsPromptsPromptContentsPartialUpdate,
          {
            organisationUuid,
            flowUuid,
            flowConfigVersion,
            id: id,
            promptPk: promptId,
            requestBody: payload,
          }
        )
        yield put({
          type: 'editPromptContentInPromptsList',
          payload: { ...response, promptId },
        })
        onSuccess()
      } catch (err) {
        FailureHandler('Could not edit prompt', err, onFailure)
      }
      setLoading(false)
    },
    *bulkEditPromptContent(
      { payload, id, promptId, setLoading = () => {}, onSuccess = () => {}, onFailure = () => {} },
      { call, select }
    ): any {
      setLoading(true)
      const organisationUuid = yield select(
        (state: any) => state.flowLayout.flow?.organisation?.uuid
      )
      const flowUuid = yield select((state: any) => state.flowLayout.flow?.uuid)
      const flowConfigVersion = yield select((state: any) => state.flowLayout.version?.version)
      const { promptContentUpdates } = payload
      let chunks = 0
      const payloadSize = 70
      message.info('Updating Prompts In Batches...')
      while (chunks + payloadSize <= promptContentUpdates.length) {
        try {
          yield call(OrganisationsService.organisationsFlowsVersionsPromptsUpdate, {
            organisationUuid,
            flowConfigVersion,
            flowUuid,
            requestBody: promptContentUpdates.slice(chunks),
          })
        } catch (e) {
          console.error('Could not update prompts ', e)
          message.error('Failed To update Prompts')
          return
        }
        chunks += payloadSize
      }
      if (chunks < promptContentUpdates.length) {
        try {
          yield call(OrganisationsService.organisationsFlowsVersionsPromptsUpdate, {
            organisationUuid,
            flowConfigVersion,
            flowUuid,
            requestBody: promptContentUpdates.slice(chunks),
          })
        } catch (e) {
          console.error('Could not update prompts ', e)
          message.error('Failed To update Prompts')
          return
        }
      }
      setLoading(false)
    },
    *bulkEditPromptNames(
      { payload, id, promptId, setLoading = () => {}, onSuccess = () => {}, onFailure = () => {} },
      { all, call, select }
    ): any {
      setLoading(true)
      const organisationUuid = yield select(
        (state: any) => state.flowLayout.flow?.organisation?.uuid
      )
      const flowUuid = yield select((state: any) => state.flowLayout.flow?.uuid)
      const flowConfigVersion = yield select((state: any) => state.flowLayout.version?.version)
      const { promptNameUpdates } = payload
      message.info('Updating Prompts In Batches...')
      const apiCallPromises = promptNameUpdates.map((p) =>
        call(OrganisationsService.organisationsFlowsVersionsPromptsPartialUpdate, {
          organisationUuid,
          flowUuid,
          flowConfigVersion,
          requestBody: { name: p.name },
          id: p.id,
        })
      )
      yield all(apiCallPromises)
      setLoading(false)
    },
    *undo({ payload }, { put, select }): any {
      const undoStack = yield select((state: any) => state.prompts.undoStack)
      if (undoStack.length === 0) {
        message.error(`Nothing to undo!`)
        return
      }
      const undoElement = undoStack[undoStack.length - 1]
      yield put({ type: 'popUndo' })
      if (!payload || !payload.undoHelpers) {
        if (process.env.NODE_ENV === 'development') {
          message.error('undoHelpers is undefined in the payload')
        }
        throw new Error('undoHelpers is undefined in the payload')
      }
      payload.undoHelpers(undoElement?.type, undoElement.data)
      yield put({ type: 'addRedo', payload: undoElement })
    },
    *redo({ payload }, { put, select }): any {
      const redoStack = yield select((state: any) => state.prompts.redoStack)
      if (redoStack.length === 0) {
        message.error(`Nothing to redo!`)
        return
      }
      const redoElement = redoStack[redoStack.length - 1]
      yield put({ type: 'popRedo' })
      if (!payload || !payload.redoHelpers) {
        if (process.env.NODE_ENV === 'development') {
          message.error('redoHelpers is undefined in the payload')
        }
        throw new Error('redoHelpers is undefined in the payload')
      }
      payload.redoHelpers(redoElement?.type, redoElement.data)
      yield put({ type: 'addUndo', payload: redoElement })
    },
  },
  reducers: {
    toggleLoading(state, action) {
      return {
        ...state,
        loading: action.payload,
      }
    },
    savePromptsList(state, action) {
      if (Array.isArray(action.payload)) {
        return {
          ...state,
          promptsList: action.payload,
        }
      }
      return state
    },
    editPromptDetails(state, action) {
      return {
        ...state,
        promptsList: state.promptsList.map((p: any) => {
          if (p.id === action.payload.id) {
            return action.payload
          }
          return p
        }),
      }
    },
    addPromptContentToPromptsList(state, action) {
      return {
        ...state,
        promptsList: state.promptsList.map((p: any) => {
          if (p.id === action.payload.promptId) {
            return {
              ...p,
              prompts: [...p.prompts, action.payload.response],
            }
          } else {
            return p
          }
        }),
      }
    },
    editPromptContentInPromptsList(state, action) {
      return {
        ...state,
        promptsList: state.promptsList.map((p: any) => {
          if (p.id === action.payload.promptId) {
            return {
              ...p,
              prompts: p.prompts.map((promptContent: any) => {
                if (promptContent.id === action.payload.id) {
                  const { prompt, ...payload } = action.payload
                  return payload
                } else {
                  return promptContent
                }
              }),
            }
          } else {
            return p
          }
        }),
      }
    },
    handleUndoRedo(state, action) {
      if (!action.payload) {
        if (process.env.NODE_ENV === 'development') {
          message.error('Undo/Redo payload is undefined')
        }
        throw new Error('Undo/Redo payload is undefined')
      }
      const { type, data } = action.payload
      const stack = state.undoStack
      if (state.undoStack.length === 0) {
        localStorage.setItem('flow_url', window.location.pathname)
      }
      stack.push({
        type,
        data,
      })
      return {
        ...state,
        undoStack: stack,
      }
    },
    addRedo(state, action) {
      const redoStack = state.redoStack
      if (!action.payload) {
        if (process.env.NODE_ENV === 'development') {
          message.error('Cannot add in redo stack, undefined payload')
        }
        throw new Error('Cannot add in redo stack, undefined payload')
      }
      redoStack.push(action.payload)
      return {
        ...state,
        redoStack: [...redoStack],
      }
    },
    popRedo(state) {
      const redoStack = state.redoStack
      if (redoStack?.length == 0) return null
      redoStack.pop()
      return {
        ...state,
        redoStack: [...redoStack],
      }
    },
    addUndo(state, action) {
      if (!action.payload) {
        if (process.env.NODE_ENV === 'development') {
          message.error('Cannot add in undo stack, undefined payload')
        }
        throw new Error('Cannot add in undo stack, undefined payload')
      }
      const undoStack = state.undoStack
      undoStack.push(action.payload)
      return {
        ...state,
        undoStack: [...undoStack],
      }
    },
    popUndo(state) {
      const undoStack = state.undoStack
      if (undoStack?.length == 0) return null
      undoStack.pop()
      return {
        ...state,
        undoStack: [...undoStack],
      }
    },
    resetPrompts(state, action) {
      return {
        ...state,
        promptsList: [],
        loading: true,
      }
    },
    clearUndoRedo(state, action) {
      return {
        ...state,
        undoStack: [],
        redoStack: [],
      }
    },
    editApplicationPrompts(state, action) {
      const { name, promptContent, promptContentId } = action.payload

      let promptsList = JSON.parse(JSON.stringify(state?.promptsList))
      let resetPromptsList = [...state.resetPromptsList]
      const overridePromptsList = new Map<string, any[]>(state.overridePrompts)

      if (action?.editType === 'reset') {
        if (typeof name === 'string' && !state.resetPromptsList.includes(name)) {
          resetPromptsList.push(name)
        } else if (Array.isArray(name)) {
          resetPromptsList = Array.from(new Set([...resetPromptsList, ...name]))
        }
        // Remove resetted prompt from overridePrompts
        const overridePromptContents = overridePromptsList.get(name)

        if (Array.isArray(overridePromptContents)) {
          const overridePrompt = overridePromptContents?.filter(
            (p) => p?.language !== promptContentId
          )
          if (overridePrompt?.length > 0) {
            overridePromptsList.set(
              name,
              overridePromptContents?.filter((p) => p?.language !== promptContentId)
            )
          } else {
            overridePromptsList.delete(name)
          }
        }
      }

      if (action?.editType === 'edit') {
        const promptContents: any[] = Array.from(state.overridePrompts.get(name) || [])
        const contentIndex = promptContents?.findIndex(
          (pc: any) => pc?.language === promptContentId
        )

        if (contentIndex > -1) {
          console.info('Already present')
          promptContents[contentIndex] = {
            ...promptContents[contentIndex],
            ...promptContent,
          }
        } else {
          console.info('New Insert')
          promptContents.push(promptContent)
        }
        overridePromptsList.set(name, promptContents)
        resetPromptsList = resetPromptsList?.filter((n) => n !== name)
        // To update default override_content when prompt content value has change
        promptsList = promptsList?.map((p: any) => {
          if (p?.name === name) {
            return {
              ...p,
              override_prompts: p?.override_prompts?.map((op: any) =>
                op?.language === promptContentId ? { ...op, content: promptContent?.content } : op
              ),
            }
          }
          return p
        })
      }

      return {
        ...state,
        promptsList,
        resetPromptsList,
        overridePrompts: overridePromptsList,
      }
    },
    resetOverridePrompts(state, action) {
      return {
        ...state,
        overridePrompts: new Map<string, any[]>(),
        resetPromptsList: [],
      }
    },
    editApplicationPromptsContent(state, action) {
      let promptsList = Array.from(state.promptsList)
      const {
        promptId,
        promptName,
        promptContentId,
        changeType,
        undoValue,
        redoValue,
        undoOrRedo,
        value,
        responseType,
      } = action?.payload
      const overridePrompts = new Map(state?.overridePrompts)
      let resetPromptsList = Array.from(state?.resetPromptsList)
      // console.info({responseType})
      promptsList = promptsList.map((p: any) => {
        if (p?.id === promptId) {
          // console.info("Macthed prompts", p)
          return {
            ...p,
            prompts: p?.prompts?.map((pc: any) => {
              if (pc?.language === promptContentId) {
                if (undoOrRedo === 'undo') {
                  if (changeType === 'edit') {
                    overridePrompts.delete(promptName)
                  }
                  if (changeType === 'reset') {
                    resetPromptsList = resetPromptsList?.filter(
                      (promptName) => promptName !== p?.name
                    )
                  }
                }
                if (undoOrRedo === 'redo') {
                  if (changeType === 'edit') {
                    const promptContents: any[] = Array.from(state.overridePrompts.get(name) || [])
                    const contentIndex = promptContents?.findIndex(
                      (pc: any) => pc?.language === promptContentId
                    )

                    if (contentIndex > -1) {
                      promptContents[contentIndex] = {
                        ...promptContents[contentIndex],
                        content: value,
                      }
                    } else {
                      promptContents.push({
                        content: value,
                        language: Number(promptContentId),
                        type: responseType,
                      })
                    }
                    overridePrompts.set(promptName, promptContents)
                  }
                  if (changeType === 'reset') {
                    if (!resetPromptsList?.includes(promptName)) {
                      resetPromptsList?.push(promptName)
                    }
                  }
                }
                return { ...pc, content: value }
              }
              return pc
            }),
            override_prompts: (p?.override_prompts || [])?.map((op: any) => {
              if (op?.language === promptContentId && changeType === 'reset') {
                return { ...op, content: value }
              }
            }),
          }
        }
        return p
      })

      return {
        ...state,
        promptsList,
        overridePrompts,
        resetPromptsList,
      }
    },
  },
}
export default Prompts
